/**
 * Turning API errors into per-field messages.
 *
 * The server answers in two shapes:
 *   express-validator -> { errors: [{ msg, path, location }] }
 *   everything else   -> { error: 'message' } or { error: 'message', field: 'name' }
 *
 * express-validator's `path` (or a plain response's own `field`) is the field
 * name (e.g. "customer.email"), which is what lets a server-side message land
 * under the input that caused it instead of in a banner at the top of the
 * form — a duplicate-name or duplicate-email rejection reads as validation,
 * not as a top-of-form error unrelated to any one field.
 */

/**
 * @param {unknown} err               axios error
 * @param {{ fieldMap?: Record<string,string>, fallback?: string }} options
 *   fieldMap translates server paths to local form field names, e.g.
 *   { 'customer.email': 'customerEmail' }.
 * @returns {Record<string,string>} field name -> message, plus `_general`
 */
export const fieldErrorsFrom = (err, options = {}) => {
  const { fieldMap = {}, fallback = 'Something went wrong. Please try again.' } = options;
  const data = err?.response?.data;
  const out = {};

  if (Array.isArray(data?.errors) && data.errors.length) {
    data.errors.forEach((item) => {
      const path = item?.path || item?.param;
      const key = (path && (fieldMap[path] || path)) || '_general';
      // Keep the first message per field: later ones are usually less specific.
      if (!out[key]) out[key] = item?.msg || fallback;
    });

    // If nothing could be attributed to a field, surface it generally rather
    // than silently swallowing the response.
    if (!Object.keys(out).length) {
      out._general = data.errors[0]?.msg || fallback;
    }
    return out;
  }

  const field = data?.field && (fieldMap[data.field] || data.field);
  if (field) {
    out[field] = data?.error || fallback;
    return out;
  }

  out._general = data?.error || fallback;
  return out;
};

/** Any message at all, for forms that only have room for one line. */
export const firstErrorFrom = (errors) => {
  if (!errors) return '';
  if (errors._general) return errors._general;
  const entry = Object.entries(errors).find(([key, value]) => key !== '_general' && value);
  return entry ? entry[1] : '';
};

/** True when the map holds no messages. */
export const hasNoErrors = (errors) =>
  !errors || Object.values(errors).every((value) => !value);
